import React, { useMemo } from 'react';
import { geoOrthographic, geoPath, geoGraticule, geoCentroid } from 'd3-geo';
import { feature } from 'topojson-client';

export default function MiniGlobe({ worldData, selectedCountryId, width = 260, height = 260 }) {
  const { pathGenerator, countries } = useMemo(() => {
    if (!worldData) return {};

    // 1. Parsowanie danych
    let featureList = [];
    if (worldData.features) {
      featureList = worldData.features;
    } else if (worldData.objects && worldData.objects.countries) {
      featureList = feature(worldData, worldData.objects.countries).features;
    }

    // 2. Szukanie wybranego kraju
    const selectedFeature = featureList.find(
      (c) => String(c.id) === String(selectedCountryId) || String(c.properties?.ISO_A3) === String(selectedCountryId)
    );

    // Domyślne wartości – większa skala początkowa
    let scale = Math.min(width, height) * 0.75;
    let rotation = [0, 0];

    // 3. Zoom i orientacja na wybrany kraj
    if (selectedFeature) {
      const centroid = geoCentroid(selectedFeature);
      rotation = [-centroid[0], -centroid[1]];

      const tempProj = geoOrthographic().scale(scale).translate([width / 2, height / 2]).rotate(rotation);
      const tempPath = geoPath().projection(tempProj);
      const bounds = tempPath.bounds(selectedFeature);
      const dx = bounds[1][0] - bounds[0][0];
      const dy = bounds[1][1] - bounds[0][1];

      if (dx > 0 && dy > 0) {
        // Zwiększony współczynnik wypełnienia ramki
        const zoomFactor = 0.75 / Math.max(dx / width, dy / height);
        scale = Math.min(Math.max(scale * zoomFactor, scale * 1.1), scale * 4);
      }
    }

    // 4. Center projekcji – bez uszczuplającego clipAngle!
    const proj = geoOrthographic()
      .scale(scale)
      .translate([width / 2, height / 2])
      .rotate(rotation);

    return {
      pathGenerator: geoPath().projection(proj),
      countries: featureList
    };
  }, [worldData, selectedCountryId, width, height]);

  if (!worldData || !pathGenerator) return null;

  const graticulePath = pathGenerator(geoGraticule()());

  return (
    <svg 
      width="100%" 
      height="100%" 
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'hidden' }}
    >
      {/* Siatka geograficzna na całą ramkę */}
      <path d={graticulePath} fill="none" stroke="#d6deeb" strokeWidth="0.6" strokeOpacity="0.5" />

      {/* Kraje */}
      <g>
        {countries.map((country, index) => {
          const countryPath = pathGenerator(country);
          if (!countryPath) return null;

          const cId = country.id || country.properties?.ISO_A3;
          const isSelected = String(cId) === String(selectedCountryId);

          return (
            <path
              key={cId || index}
              d={countryPath}
              fill={isSelected ? '#5c85c6' : '#d1d5db'}
              stroke="#ffffff"
              strokeWidth={isSelected ? '1.5' : '0.5'}
            />
          );
        })}
      </g>
    </svg>
  );
}